import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';

const useIso = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/* ------------------------------------------------------------------
   Геометрія знята покадрово з референсу (сцена 888px шириною,
   дільник до реальних px: 2.28). Ключові кадри в K: [стан "Банк",
   стан "Маркет"]. Поочергове зникнення пунктів — це обрізка контенту
   краями капсул (overflow:hidden на Pill), а не індивідуальний fade.

   ITEM_W/ICON/... тут — саме розміри, з якими рахується позиція
   (translateX). Візуально ті самі числа зашиті в Navbar.css класами
   .navmorph-item/.navmorph-hit/.navmorph-icon-box/.navmorph-label —
   якщо міняєш тут, онови й там.
   ------------------------------------------------------------------ */
const W = 888;
const NAV_H = 194;
const ITEM_W = 260; // клікабельна ширина — окремо в CSS (.navmorph-hit, вужче за ITEM_W)

const K = {
  p1L: [47, 39], p1R: [677, 177],
  p2L: [703, 202], p2R: [841, 685],
  p3L: [147, 712], p3R: [92, 848],
  cards: [143, 108],
  credits: [289, 254],
  savings: [440, 405],
  more: [590, 555],
  market: [772, 298],
  cabinet: [918, 444],
  cart: [1064, 590],
};

// Крива прогресу знята з gif покадрово (пружний виліт наприкінці).
const CURVE = [0, 0.219, 0.392, 0.553, 0.694, 0.806, 0.892, 0.952, 0.992,
  1.013, 1.025, 1.030, 1.027, 1.025, 1.019, 1.013, 1.008,
  1.006, 1.004, 1.002, 1];
const DUR = 350;

const ease = (t) => {
  const n = CURVE.length - 1;
  const x = Math.min(Math.max(t, 0), 1) * n;
  const i = Math.min(Math.floor(x), n - 1);
  return CURVE[i] + (CURVE[i + 1] - CURVE[i]) * (x - i);
};
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

const Item = React.forwardRef(function Item(
  { onClick, iconSrc, iconAltSrc, iconAlt, label, labelClass, alt, altClass, oldRef, newRef, iconOldRef, iconNewRef },
  ref
) {
  return (
    <div ref={ref} className="navmorph-item">
      <div
        className={`navmorph-hit ${onClick ? 'navmorph-hit--clickable' : ''}`}
        onClick={onClick}
      >
        {iconAltSrc ? (
          <>
            <div ref={iconOldRef} className="navmorph-icon-box"><img src={iconSrc} alt={iconAlt} className="navmorph-icon-img" /></div>
            <div ref={iconNewRef} className="navmorph-icon-box" style={{ opacity: 0 }}><img src={iconAltSrc} alt={iconAlt} className="navmorph-icon-img" /></div>
          </>
        ) : (
          <div className="navmorph-icon-box"><img src={iconSrc} alt={iconAlt} className="navmorph-icon-img" /></div>
        )}
        <p ref={oldRef} className={`navmorph-label ${labelClass || ''}`}>{label}</p>
        {alt && <p ref={newRef} className={`navmorph-label ${altClass || ''}`} style={{ opacity: 0 }}>{alt}</p>}
      </div>
    </div>
  );
});

const Pill = React.forwardRef(function Pill({ children, index }, ref) {
  return (
    <div ref={ref} className={`navmorph-pill navmorph-pill--${index}`} style={{ left: 0, width: 0 }}>
      {children}
    </div>
  );
});

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMarket = location.pathname.startsWith('/market');
  // Активні вкладки маркету (підсвітка акцентом #5b6bf5)
  const isMarketHome = location.pathname === '/market';
  const isCabinet = location.pathname === '/market/cabinet';
  const isCart = location.pathname === '/market/cart';
  const isBazaar = location.pathname === '/market/purchases';
  const isCredit = location.pathname === '/credit';
  // Історія, статистика й деталі транзакції — підекрани вкладки «Картки», тож
  // на них активним лишається саме цей таб (інакше підсвітка зникає під час слайду).
  const isCards = location.pathname === '/'
    || location.pathname === '/history'
    || location.pathname === '/statistics'
    || location.pathname.startsWith('/transaction/');
  const isSavings = location.pathname === '/savings';
  const isMore = location.pathname === '/more';

  const box = useRef(null);
  const [scale, setScale] = useState(1);
  const r = useRef({});
  const p = useRef(0);
  const raf = useRef(0);
  const mounted = useRef(false);
  const reg = (k) => (el) => { r.current[k] = el; };

  useIso(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / W);
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const paint = useCallback((v) => {
    const e = r.current;
    if (!e.p1) return;
    const L = (k) => K[k][0] + (K[k][1] - K[k][0]) * v;

    const p1L = L('p1L'), p1R = L('p1R');
    const p2L = L('p2L'), p2R = L('p2R');
    const p3L = L('p3L'), p3R = L('p3R');
    const HW = ITEM_W / 2;

    const put = (pill, l, rr) => {
      const w = Math.max(0, rr - l);
      pill.style.left = l + 'px';
      pill.style.width = w + 'px';
      // Схлопнута пілюля (ширина ~0) інакше показує свій бордер тонкою
      // вертикальною лінією поверх сусідньої — ховаємо її.
      pill.style.visibility = w < 1 ? 'hidden' : 'visible';
    };
    put(e.p1, p1L, p1R);
    put(e.p2, p2L, p2R);
    put(e.p3, p3L, p3R);

    const at = (el, cx, pl) => { el.style.transform = `translateX(${cx - pl - HW}px)`; };
    at(e.cards, L('cards'), p1L);
    at(e.credits, L('credits'), p1L);
    at(e.savings, L('savings'), p1L);
    at(e.more, L('more'), p1L);
    at(e.market, L('market'), p2L);
    at(e.cabinet, L('cabinet'), p2L);
    at(e.cart, L('cart'), p2L);
    at(e.bazaar, (p3L + p3R) / 2, p3L);

    // підписи й іконки, що змінюють сенс (Картки↔Банк, Маркет↔Головна):
    // crossfade у вузькому вікні прогресу
    const out = clamp01((0.95 - v) / 0.33);
    const inn = clamp01((v - 0.62) / 0.33);
    e.cardsOld.style.opacity = out;
    e.cardsNew.style.opacity = inn;
    e.marketOld.style.opacity = out;
    e.marketNew.style.opacity = inn;
    e.marketIconOld.style.opacity = out;
    e.marketIconNew.style.opacity = inn;

    e.badge.style.opacity = clamp01(v / 0.15);
  }, []);

  // миттєва синхронізація без анімації при першому монтуванні —
  // якщо зайшли одразу на /market, навбар має початись вже в тому стані
  useIso(() => {
    p.current = isMarket ? 1 : 0;
    paint(p.current);
    mounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useIso(() => { paint(p.current); }, [paint, scale]);

  const run = useCallback((target) => {
    cancelAnimationFrame(raf.current);
    const from = p.current;
    const span = target - from;
    if (Math.abs(span) < 0.001) return;
    const reduce = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { p.current = target; paint(target); return; }
    const dur = DUR * Math.abs(span);
    const t0 = performance.now();
    const step = (now) => {
      const t = (now - t0) / dur;
      const v = t >= 1 ? target : from + span * ease(t);
      p.current = v;
      paint(v);
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, [paint]);

  useEffect(() => {
    if (!mounted.current) return;
    run(isMarket ? 1 : 0);
  }, [isMarket, run]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  return (
    <>
      <div className="navbar-morph-shade" />
      <div ref={box} className="navbar-morph-box">
        <div className="navbar-morph-scene" style={{ width: W * scale, height: NAV_H * scale }}>
          <div className="navbar-morph-inner" style={{ transform: `scale(${scale})` }}>
          <Pill ref={reg('p1')} index={1}>
            <Item
              ref={reg('cards')}
              onClick={() => navigate('/')}
              iconSrc={isCards ? '/nav-cards.png' : '/card-unactive.webp'} iconAlt="Картки"
              label="Картки" labelClass={isCards ? 'navmorph-label--active' : ''}
              oldRef={reg('cardsOld')}
              alt="Банк" newRef={reg('cardsNew')}
            />
            <Item
              ref={reg('credits')}
              onClick={() => navigate('/credit')}
              iconSrc={isCredit ? '/credit-icons/credit-nav-icon.webp' : '/credit.png'} iconAlt="Кредити"
              label="Кредити" labelClass={isCredit ? 'navmorph-label--active' : ''}
              oldRef={reg('x1')}
            />
            <Item
              ref={reg('savings')}
              onClick={() => navigate('/savings')}
              iconSrc={isSavings ? '/saving-active.webp' : '/saving.png'} iconAlt="Накопичення"
              label="Накопичення" labelClass={isSavings ? 'navmorph-label--active' : ''}
              oldRef={reg('x2')}
            />
            <Item
              ref={reg('more')}
              onClick={() => navigate('/more')}
              iconSrc={isMore ? '/other-icons/other-icon.webp' : '/more.png'} iconAlt="Ще"
              label="Ще" labelClass={isMore ? 'navmorph-label--active' : ''}
              oldRef={reg('x3')}
            />
          </Pill>

          <Pill ref={reg('p2')} index={2}>
            <Item
              ref={reg('market')}
              onClick={() => navigate('/market')}
              iconSrc="/market.png" iconAltSrc={isMarketHome ? '/market-icons/market-bag.webp' : '/market-icons/market-bag-inactive.webp'} iconAlt="Маркет"
              iconOldRef={reg('marketIconOld')} iconNewRef={reg('marketIconNew')}
              label="Маркет"
              oldRef={reg('marketOld')}
              alt="Головна" altClass={isMarketHome ? 'navmorph-label--accent' : ''} newRef={reg('marketNew')}
            />
            <Item
              ref={reg('cabinet')}
              onClick={() => navigate('/market/cabinet')}
              iconSrc={isCabinet ? '/market-icons/market-love-active.webp' : '/market-icons/market-love.webp'} iconAlt="Кабінет"
              label="Кабінет" labelClass={isCabinet ? 'navmorph-label--accent' : ''}
              oldRef={reg('x4')}
            />
            <Item
              ref={reg('cart')}
              onClick={() => navigate('/market/cart')}
              iconSrc={isCart ? '/market-icons/market-cart-active.webp' : '/market-icons/market-cart.webp'} iconAlt="Кошик"
              label="Кошик" labelClass={isCart ? 'navmorph-label--accent' : ''}
              oldRef={reg('x5')}
            />
          </Pill>

          <Pill ref={reg('p3')} index={3}>
            <Item
              ref={reg('bazaar')}
              onClick={() => navigate('/market/purchases')}
              iconSrc="/market-icons/market-bazar.webp" iconAlt="Базар"
              label="Базар" labelClass={isBazaar ? 'navmorph-label--accent' : ''}
              oldRef={reg('x6')}
            />
          </Pill>

          <div ref={reg('badge')} className="navbar-morph-badge">BETA</div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Navbar;
